/**
 * CcqStaleRunsBanner — périodes calculées avant la dernière modification de la configuration CCQ : recalcul groupé des périodes ouvertes,
 * liste des périodes verrouillées (approuvées / payées) à rouvrir d'abord. Batch #14.304.
 */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { AlertTriangle, RefreshCw, X } from "lucide-react";
import { toast } from "@/lib/toast";
import { API, authH, Btn, err, FREQ_LABELS } from "../_shared";

export function useStaleRuns(masterId) {
  const [stale, setStale] = useState(null);
  const load = useCallback(async () => {
    try { const r = await axios.get(`${API}/admin/payroll/${masterId}/ccq/stale-runs`, { headers: authH() }); setStale(r.data); } catch (e) { err(e); }
  }, [masterId]);
  useEffect(() => { load(); }, [load]);
  return [stale, load];
}

export async function recalculateOpenRuns(masterId, runIds = []) {
  const r = await axios.post(`${API}/admin/payroll/${masterId}/ccq/recalculate-runs`, { run_ids: runIds }, { headers: authH() });
  const d = r.data;
  const nbIssues = d.recalculated.reduce((n, x) => n + (x.ccq_issues || []).length, 0);
  toast.success(`${d.recalculated.length} période(s) recalculée(s)${d.locked.length ? ` · ${d.locked.length} verrouillée(s) non touchée(s)` : ""}${nbIssues ? ` · ${nbIssues} cotisation(s) encore non appliquée(s)` : ""}`);
  return d;
}

export default function CcqStaleRunsBanner({ masterId, stale, onDone, compact = false }) {
  const [busy, setBusy] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const key = `ccq-stale-dismiss-${masterId}`;
  useEffect(() => { if (stale) setDismissed(window.localStorage.getItem(key) === String(stale.config_updated_at)); }, [stale, key]);
  if (!stale || (!stale.open.length && !stale.locked.length)) return null;
  if (!stale.open.length && dismissed) return null;
  const run = async () => {
    setBusy(true);
    try { await recalculateOpenRuns(masterId); onDone?.(); } catch (e) { err(e); } finally { setBusy(false); }
  };
  const dismiss = () => { window.localStorage.setItem(key, String(stale.config_updated_at)); setDismissed(true); };
  const fmt = (r) => `${r.period_start} → ${r.period_end} (${FREQ_LABELS[r.frequency] || r.frequency}${r.nb_employees ? ` · ${r.nb_employees} empl.` : ""})`;
  const onlyLocked = !stale.open.length;
  return (
    <div className={`${onlyLocked ? "bg-slate-50 border-slate-200 text-slate-700" : "bg-rose-50 border-rose-200 text-rose-900"} border rounded-xl p-3 text-xs space-y-2`} data-testid="paie-ccq-stale-banner">
      <div className="flex items-start gap-2">
        <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
        <div className="flex-1">
          <b>Configuration CCQ modifiée le {String(stale.config_updated_at).slice(0, 16).replace("T", " ")} — {stale.open.length} période(s) ouverte(s) calculée(s) avec d'anciennes règles{stale.locked.length ? `, ${stale.locked.length} verrouillée(s)` : ""}.</b>
          {!compact && stale.open.length > 0 && <div className="mt-1 text-rose-800/90" data-testid="paie-ccq-stale-open">{stale.open.map(fmt).join(" · ")}</div>}
          {stale.locked.length > 0 && (
            <div className={`mt-1 ${onlyLocked ? "text-slate-600" : "text-rose-800/80"}`} data-testid="paie-ccq-stale-locked">
              Approuvées / payées, calculées avant la modification (à rouvrir dans Périodes de paie puis recalculer, seulement si elles sont concernées) : {stale.locked.map(fmt).join(" · ")}
            </div>
          )}
        </div>
        {stale.open.length > 0
          ? <Btn tone="rose" icon={RefreshCw} onClick={run} busy={busy} testId="paie-ccq-recalc-all">Recalculer {stale.open.length} période(s) ouverte(s)</Btn>
          : <Btn tone="slate" icon={X} onClick={dismiss} testId="paie-ccq-stale-dismiss">Masquer</Btn>}
      </div>
    </div>
  );
}
